import { Component, OnInit, Input, Output, OnChanges, ViewChild, AfterViewInit, EventEmitter } from '@angular/core';
import { NgbModal, NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FiaService } from 'src/app/services/fia.service';
import { MediaService } from 'src/app/services/media.service';
import { SettingsService } from 'src/app/services/settings.service';
import { UserService } from 'src/app/services/user.service';
import { LookupFiaStatus } from 'src/app/shared/models/general/lookup-fia-status';
import { LookupMediaTypes } from 'src/app/shared/models/general/lookup-media-types';
import { Media } from 'src/app/shared/models/general/media';
import { MediaInfo } from 'src/app/shared/models/general/media-info';
import { UserFullDetails } from 'src/app/shared/models/users/user-full-details';
import { UserProfile } from 'src/app/shared/models/users/user-profile';
import { Users } from 'src/app/shared/models/users/users';

@Component({
  selector: 'app-user-documents',
  templateUrl: './user-documents.component.html',
  styleUrls: ['./user-documents.component.scss'],
})
export class UserDocumentsComponent implements OnInit, AfterViewInit, OnChanges {
  @ViewChild('fiaDocumentsModal', { static: true }) fiaDocumentsModal: any;
  @ViewChild('file') file;

  @Input() selectedUserProfile: UserProfile;
  @Input() userDocuments: Media[];
  @Input() showModal: boolean = false;
  @Output() showModalChange = new EventEmitter<boolean>();
  @Output() onModelChange = new EventEmitter<UserProfile>();

  busy: boolean = false;
  showError: boolean;
  errorMsg: string = '';
  fiaStatus: LookupFiaStatus[] = [];
  mediaTypes: LookupMediaTypes[] = [];

  userProfile: UserProfile = new UserProfile();
  userNewDocuments: MediaInfo[] = [];
  userDocumentsToDelete: number[] = [];
  documentToDeleteName: string;
  showDeleteConfirmation: boolean = false;
  docTypeOptions: doc[] = [];

  constructor(
    public settingsService: SettingsService,
    public usersService: UserService,
    public activeModal: NgbActiveModal,
    public fiaService: FiaService,
    public mediaService: MediaService
  ) {
    this.clearErrors();
  }

  ngOnInit(): void {
    this.generateDocTypeOptions();
  }

  ngOnChanges() {
    this.showHideModal(null);
  }

  ngAfterViewInit() {}

  determineFileTypeAndVersion(value: string, doc: MediaInfo) {
    if (value.includes(' - ')) {
      let splitString = value.split(' - ');
      doc.fileType = splitString[0];
      doc.version = parseInt(splitString[1], 10);
    } else {
      doc.fileType = value;
      doc.version = 1;
    }
  }

  generateDocTypeOptions() {
    const currentYear = new Date().getFullYear();
    this.docTypeOptions.push(
      {
        type_version: `Crypto Declaration - ${currentYear}`,
      },
      {
        type_version: `Crypto Declaration - ${currentYear + 1}`,
      },
      {
        type_version: 'Marriage Certificate',
      },
      {
        type_version: 'Trade Source Of Funds',
      }
    );
  }

  onClosed(event: any) {
    if (this.showModal != false) {
      this.showModal = false;
      this.showModalChange.emit(this.showModal);
    }
  }

  showHideModal(toggle: boolean) {
    if (toggle != null) {
      this.showModal = toggle;
    }

    if (this.showModal) {
      this.fiaDocumentsModal.show();
      this.userProfile = <UserProfile>JSON.parse(JSON.stringify(this.selectedUserProfile));
      this.filterUserDocuments(this.userDocuments);
    } else if (!this.showModal) {
      // Blur any focused element before hiding modal to prevent aria-hidden warning
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
      this.clearErrors();
      this.userNewDocuments = [];
      this.fiaDocumentsModal.hide();
    }

    this.showModalChange.emit(this.showModal);
  }

  GetUserMedia() {
    this.busy = true;
    this.mediaService.GetMediaByEntityType(this.settingsService.userEntity.description, this.selectedUserProfile.userId).subscribe(
      (response) => {
        this.filterUserDocuments(response);
        this.busy = false;
      },
      (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = false;
      }
    );
  }

  filterUserDocuments(docs: Media[]) {
    this.userDocuments = docs.filter(
      (x) => x.fileType == 'Crypto Declaration' || x.fileType == 'Marriage Certificate' || x.fileType == 'Trade Source Of Funds'
    );
  }

  DownloadFile(id: number) {
    this.busy = true;
    this.mediaService.DownloadMedia(id).subscribe(
      (response) => {
        this.mediaService.Download(response);
        this.busy = false;
      },
      (error) => {
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
        this.busy = false;
      }
    );
  }

  clearErrors() {
    this.showError = false;
    this.errorMsg = '';
  }

  onFileChange(fileInput: any) {
    this.clearErrors();
    this.userNewDocuments = [];
    var totalSize: number = 0;
    var maxSize: number = 5000000;
    const files: { [key: string]: File } = this.file.nativeElement.files;
    for (let key in files) {
      if (!isNaN(parseInt(key))) {
        var reader = new FileReader();
        reader.onload = (event: any) => {
          if (totalSize <= maxSize && files[key].size <= maxSize) {
            var file: MediaInfo = new MediaInfo();
            file.name = files[key].name;
            file.fileName = files[key].name;
            file.mimeType = files[key].type;
            file.tag = '';
            file.data = event.target.result;
            file.data = file.data.replace('data:' + files[key].type + ';base64,', '');
            file.entityId = this.userProfile.userId;
            file.entityType = this.settingsService.userEntity.description; // Users
            totalSize += files[key].size;
            this.userNewDocuments.push(file);
          } else {
            this.showError = true;
            this.errorMsg = 'Max file upload size reached';
          }
        };

        reader.readAsDataURL(files[key]);
      }
    }
  }

  removeFile(fileName: string) {
    this.busy = true;
    this.userNewDocuments.forEach((value, index) => {
      if (value.fileName == fileName) {
        this.userNewDocuments.splice(index, 1);
      }
    });
    this.busy = false;
  }

  confirmDelete(media: Media) {
    this.userDocumentsToDelete.push(media.id);
    this.documentToDeleteName = media.name;
    this.showDeleteConfirmation = true;
  }

  cancelDelete() {
    this.userDocumentsToDelete = [];
    this.showDeleteConfirmation = false;
  }

  confirmDeleteModalYesClicked() {
    this.busy = true;
    this.DeleteUserMedia().then(
      () => {
        this.showDeleteConfirmation = false;
        this.GetUserMedia();
      },
      (error) => {
        this.showError = true;
        this.busy = false;
        this.showDeleteConfirmation = false;
      }
    );
  }

  public DeleteUserMedia() {
    if (!this.userDocumentsToDelete || this.userDocumentsToDelete.length == 0) {
      return new Promise((resolve) => {
        resolve(true);
      });
    }

    return Promise.all(this.userDocumentsToDelete.map(async (v) => this.mediaService.DeleteMediaAsync(v))).then(
      () => {},
      (rejected) => {}
    );
  }

  newSupportingDocumentsModalSaveClicked() {
    this.clearErrors();
    if (this.missingDocumentTypes()) {
      this.showError = true;
      this.errorMsg = 'Please select a type for all documents.';
      return;
    }

    this.busy = true;
    this.UploadUserMedia().then(
      () => {
        //Upload new docs
        this.busy = false;
        this.GetUserMedia();
        this.userNewDocuments = [];
      },
      (error) => {
        this.busy = false;
        this.showError = true;
        this.errorMsg = error.error.ErrorDetail;
      }
    );
  }

  missingDocumentTypes(): boolean {
    return userDocumentsMissingType(this.userNewDocuments);
  }

  public UploadUserMedia() {
    if (!this.userNewDocuments || this.userNewDocuments.length == 0) {
      return new Promise((resolve) => {
        resolve(true);
      });
    }

    return Promise.all(this.userNewDocuments.map(async (v) => this.mediaService.UploadMediaAsync(v))).then(
      () => {},
      (rejected) => {}
    );
  }
}
class doc {
  type_version: string;
}

export function userDocumentsMissingType(docs: { fileType?: string | null }[]): boolean {
  return docs.some((doc) => !doc.fileType?.trim());
}
